import { useState, useRef, useEffect } from "react";
import {
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { firestored, storage, app } from "../../firebase/firebaseConfig";
import {
  addChatIdToDeal,
  getAlldealforAdmin,
  getAllEmployeeTasks,
  getDealById,
  saveitemwithkey,
} from "../../utils/firebasefunctions";
import moment from "moment";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { Paperclip } from "lucide-react";
import { actionType } from "../../context/reducer";
import { useNavigate } from "react-router-dom";
import ProfilePage from "./profile-popup";
import {
  extractMentionedIds,
  getFullName,
  renderWithMentions,
  useMentionAutocomplete,
} from "../../utils/mentions";
import { notifyMentions } from "../../utils/mentionNotify";

const Chat = ({ data }) => {
  const [{ user, users }, dispatch] = useStateValue();
  const navigate = useNavigate();
  const [message, setmessage] = useState("");
  const [messagearray, setmessagearray] = useState([]);
  const [chatid, setchatid] = useState("");
  const [chats, setchats] = useState([]);
  const [picturedata, setpicturedata] = useState("");
  const [picture, setpicture] = useState([]);
  const [picturePreview, setpicturePreview] = useState([]);
  const endOfMessagesRef = useRef(null);
  const endOfMessagesRefmobile = useRef(null);
  const [loader, setloader] = useState(false);
  const [deal, setDeal] = useState(null);
  const [dealLoading, setDealLoading] = useState(true);
  const [isProfile, setIsProfile] = useState(false);
  const [mentionProfileId, setMentionProfileId] = useState("");

  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-grow the composer so longer messages don't spill outside the box.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [message]);

  const fetchDeal = async () => {
    if (!data.createdat) return;

    const dealData = await getDealById(String(data.createdat)); // Function call
    setDeal(dealData);
    setDealLoading(false);
  };

  // 🔹 Set chatid from deal on mount
  useEffect(() => {
    if (dealLoading) return;
    if (deal?.chatId) {
      setchatid(deal.chatId);
    } else {
      checkconversationexistance();
    }
  }, [deal, dealLoading]);

  async function getalldeal() {
    await getAlldealforAdmin("deals")
      .then(async (response) => {
        dispatch({
          type: actionType.STORE_DEALS,
          deals: response,
        });
      })
      .catch((err) => {
        toast.error(String(err?.message || err));
      });
  }

  async function checkconversationexistance() {
    await getAllEmployeeTasks(
      "conversations",
      "owners",
      "array-contains",
      deal.createdat,
    )
      .then(async (response) => {
        if (response.length > 0) {
          response.forEach((doc) => {
            setchatid(doc.id);
          });
        } else {
          const nowMs = Date.now();
          let chatkey =
            "conversation_" + nowMs + "_" + Math.floor(Math.random() * 1000);
          let EditedTimestamp = moment(new Date()).format(
            "YYYY-MM-DD hh:mm:ss a",
          );
          let owners = [];
          // owners.push(user?.id);

          let chatdata = {
            createdTimestamp: EditedTimestamp,
            id: chatkey,
            latestMessageTimeMs: EditedTimestamp,
            owners: owners,
          };
          await saveitemwithkey("conversations", chatkey, chatdata)
            .then(async (response) => {
              if (response === "success") {
                await addChatIdToDeal(String(deal.createdat), chatdata.id);
                getalldeal();
                setchatid(chatkey);
              } else {
                toast.error("Something went wrong. Please try again.");
              }
            })
            .catch((error) => {
              toast.error(String(error?.message || error));
            });
        }
      })
      .catch((err) => {
        toast.error(String(err?.message || err));
      });
  }

  const sendmessage = async () => {
    if ((message?.trim() === "" || !message) && picturedata.length === 0) {
      toast.warn("You cannot send a blank message");
      return;
    }

    setloader(true);
    const nowMs = Date.now();
    const messageId =
      "messageText_" + nowMs + "_" + Math.floor(Math.random() * 100000);
    const EditedTimestamp = moment(new Date()).format("YYYY-MM-DD hh:mm:ss a");

    let imagelinks = [];

    if (picturedata.length > 0) {
      try {
        for (const file of picturedata) {
          const storageRef = ref(
            storage,
            `chat-images/${chatid}/${Date.now()}-${file.name}`,
          );

          const uploadTaskSnapshot = await uploadBytesResumable(
            storageRef,
            file,
          );

          const url = await getDownloadURL(uploadTaskSnapshot.ref);

          imagelinks.push(url);
        }
      } catch (err) {
        // Without this the Send button spins forever on a failed upload and
        // the only way out is reloading the page.
        setloader(false);
        toast.error("Image upload failed: " + err.message);
        return;
      }
    }

    // Read the tags straight off the text being sent. The old code kept a
    // parallel list of ids as you clicked names, which could disagree with
    // what the box actually said.
    const mentions = [...extractMentionedIds(message, users)].filter(
      (id) => id !== user?.id,
    );

    // 🔹 Prepare Message Object
    const messagedata = {
      id: messageId,
      createdMs: nowMs,
      createdTimestamp: EditedTimestamp,
      owner: user?.id,
      text: message || "",
      hasImages: imagelinks.length > 0,
      hasText: !!message,
      image: imagelinks || "",
      mentions: mentions,
    };

    const displaymessage = {
      latestMessageTimeMs: nowMs,
      latestmessage: message || (imagelinks ? "Image" : ""),
      messages: arrayUnion(messagedata),
      latestmessageseen: false,
      owners: arrayUnion(user?.id),
    };

    // 🔹 Save to Firestore
    try {
      await saveitemwithkey("conversations", chatid, displaymessage);
      // await saveitemwithkey("conversations", chatid, messagedata);
      setmessage("");
      setpicturedata([]);
      setpicturePreview([]);
      if (mentions.length > 0) {
        const dealLocation = deal?.propertyaddress || "a deal";
        notifyMentions({
          text: messagedata.text,
          users: users.filter((u) => mentions.includes(u.id)),
          sender: user,
          where: `the chat on ${dealLocation}`,
          dealId: String(deal?.id || deal?.createdat || ""),
        }).catch((err) => console.error("Mention notification failed:", err));
      }
      setloader(false);
    } catch (err) {
      toast.error("Message send failed: " + err.message);
    } finally {
      setloader(false);
    }
  };

  const mention = useMentionAutocomplete({
    text: message,
    setText: setmessage,
    users,
    currentUserId: user?.id,
    fieldRef: textareaRef,
  });

  // Both renderers go through the shared tokenizer, which hands back React
  // nodes. The previous versions built an HTML string and dropped it in with
  // dangerouslySetInnerHTML, so anything typed into a deal chat was injected
  // into the page as markup. data-userid stays on the span because the click
  // handler above it opens that person's profile popup.
  const mentionNodes = (text) =>
    renderWithMentions(text, users).map((token, i) =>
      typeof token === "string" ? (
        <span key={i}>{token}</span>
      ) : (
        <span
          key={i}
          data-userid={token.mention.id}
          className="text-blue-600 font-medium cursor-pointer"
        >
          @{getFullName(token.mention)}
        </span>
      ),
    );

  const renderMessageWithMentions = (text) => (text ? mentionNodes(text) : text);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // newline prevent karega
      sendmessage();
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    if (!files.length) return;

    setpicturedata((prev) => [...prev, ...files]);

    const previews = files.map((file) => URL.createObjectURL(file));

    setpicturePreview((prev) => [...prev, ...previews]);
  };

  useEffect(() => {
    // Scroll to the bottom of the chat when new messages arrive
    endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth" });
    endOfMessagesRefmobile.current?.scrollIntoView({ behavior: "smooth" });
  }, [messagearray]);

  useEffect(() => {
    if (dealLoading) return;

    if (chatid !== "" && chatid !== undefined) {
      const subscriber = onSnapshot(
        doc(firestored, "conversations", chatid),
        (doc) => {
          if (doc.data()?.messages?.length > 0) {
            setmessagearray(doc.data().messages);
          } else {
            setmessagearray([]);
          }
        },
      );

      // Cleanup subscription on unmount
      return () => subscriber();
    }
  }, [chatid, dealLoading]);

  useEffect(() => {
    fetchDeal();
  }, [data.createdat]);

  if (dealLoading) {
    return (
      <div className="p-10 text-center text-gray-500">Loading chat...</div>
    );
  }

  return (
    <>
      <div className="flex flex-col lg:h-full bg-gray-50 relative">
        <div className="bg-white shadow-md p-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Chat</h2>
        </div>

        <div className="flex-1 overflow-y-auto p-4 panelScroll space-y-4">
          {messagearray.map((item, index) => {
            const messageUser = users.find((u) => u.id === item.owner);

            return (
              <div
                key={index.toString()}
                className={`my-4 flex flex-col ${
                  item.owner === user?.id ? "items-end" : "items-start"
                }`}
              >
                <div className="flex">
                  <div
                    className={`pl-4 flex flex-col ${
                      item.owner === user?.id ? "items-end" : "items-start"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {/* 👇 User Profile Image */}
                      {item.owner !== user?.id && (
                        <img
                          src={
                            messageUser?.profilepicture || "/assets/user.png"
                          }
                          className="w-[35px] h-[35px] rounded-full object-cover mt-1"
                          alt="user"
                        />
                      )}

                      <div
                        className={`${
                          item?.hasImages
                            ? ""
                            : item.owner === user?.id
                              ? "bg-[#1E293B] text-white rounded-br-none"
                              : "bg-gray-200 text-gray-800 rounded-bl-none"
                        } ${
                          item?.hasImages ? "" : "px-5"
                        } text-textcolor py-2 rounded-full`}
                      >
                        <div className="flex flex-col gap-2">
                          {item?.image?.length > 0 &&
                            item.image.map((img, i) => (
                              <img
                                key={i}
                                src={img}
                                alt=""
                                className="w-[200px] h-[100px] object-cover rounded-lg"
                              />
                            ))}

                          {item?.text && (
                            <p
                              ref={endOfMessagesRef}
                              className="text-sm PoppinsRegular"
                              onClick={(e) => {
                                const target = e.target;

                                if (target.dataset.userid) {
                                  const userId = target.dataset.userid;
                                  setIsProfile(true);
                                  setMentionProfileId(userId);
                                }
                              }}
                            >
                              {renderMessageWithMentions(item.text)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <p
                      className={`PoppinsRegular pt-1 text-[10px] text-textcolor ${
                        item.owner === user?.id ? "mr-[10px]" : "ml-[10px]"
                      }`}
                    >
                      {moment(item.createdMs).fromNow()}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <div className="bg-white p-4 flex items-center gap-2 border-t border-gray-200 sticky bottom-0 z-10">
          {/* Image Preview */}
          {picturePreview.length > 0 && (
            <div className="absolute bottom-[70px] left-4 bg-white shadow-lg rounded-lg p-2 border flex gap-2">
              {picturePreview.map((preview, index) => (
                <div key={index} className="relative">
                  <img
                    src={preview}
                    alt=""
                    className="w-[100px] h-[80px] object-cover rounded"
                  />

                  <button
                    onClick={() => {
                      const newPreview = picturePreview.filter(
                        (_, i) => i !== index,
                      );
                      const newFiles = picturedata.filter(
                        (_, i) => i !== index,
                      );

                      setpicturePreview(newPreview);
                      setpicturedata(newFiles);
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {mention.showMentions && mention.candidates.length > 0 && (
            <div className="absolute bottom-[70px] left-4 bg-white border rounded shadow-md max-h-[200px] overflow-y-auto w-[250px] z-50">
              {mention.candidates.map((u) => (
                <div
                  key={u.id}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => mention.selectMention(u)}
                  className="p-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                >
                  <img
                    src={u.profilepicture || "/assets/user.png"}
                    alt=""
                    className="w-6 h-6 rounded-full"
                  />
                  <span>{getFullName(u)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Message Input */}
          <div className="relative flex-1">
            {/* 👇 Highlight Layer */}
            <div className="absolute inset-0 px-4 py-2 text-black whitespace-pre-wrap break-words pointer-events-none">
              {mentionNodes(message)}
            </div>

            {/* 👇 Real Input */}
            <textarea
              ref={textareaRef}
              value={message}
              onChange={mention.fieldHandlers.onChange}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className="relative w-full border rounded-2xl px-4 py-2 bg-transparent text-transparent caret-black focus:outline-none focus:ring-2 focus:ring-[#1E293B] resize-none overflow-hidden"
              rows={1}
            />
          </div>
          {/* <input
            type="text"
            value={message}
            onChange={handlemessageChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="flex-1 border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#1E293B]"
          /> */}

          {/* Image Upload */}
          <label className="cursor-pointer">
            <Paperclip />
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              className="hidden"
            />
          </label>

          {/* Send Button */}
          <button
            onClick={sendmessage}
            className="bg-[#1E293B] text-white px-4 py-2 rounded-full flex items-center gap-x-1"
          >
            Send
            {loader && (
              <div role="status" className="">
                <svg
                  aria-hidden="true"
                  class="w-4 h-4 text-gray-200 animate-spin dark:text-white fill-[#1E293B]"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span class="sr-only">Loading...</span>
              </div>
            )}
          </button>
        </div>
      </div>
      {isProfile && (
        <ProfilePage
          userId={mentionProfileId}
          onclose={() => setIsProfile(false)}
        />
      )}
    </>
  );
};

export default Chat;
